// The new-contract onboarding checklist (Session AQW, Phase 2 office-UI restructure, W-11).
// Purely presentational: every item's state is derived LIVE, server-side, from columns and
// relations the platform already writes (server/src/modules/contracts/onboarding.ts, RULE 6 --
// nothing here is stored a second time). This component only supplies the plain-English label per
// key and the tick / pending / not-applicable treatment.
import type { OnboardingItem, OnboardingKey } from "../lib/api";
import { StatusPill } from "./StatusPill";
import { formatDate } from "../lib/format";

const LABEL: Record<OnboardingKey, string> = {
  quote_accepted: "Quote accepted",
  esa_sent: "ESA sent",
  signed: "Signed",
  ele_numbers: "ELE numbers allocated",
  drive_filed: "Drive folder / signed PDF filed",
  xero_draft: "Xero draft invoice raised",
  welcome_email: "Welcome email drafted",
  portal_user: "Portal user created",
  rfid_tags: "RFID tag(s) live",
  first_ppm_visit: "First PPM visit generated",
};

// first_ppm_visit only: engineer assignment is a separate, known gap (D84 -- ppmGenerateTrigger.ts
// hardcodes assignedTo: null), so this item deliberately answers only "does a visit exist yet" and
// says where assignment actually happens, rather than reading as a permanently-red step.
const NOTE: Partial<Record<OnboardingKey, string>> = {
  first_ppm_visit: "Engineer assignment is done from Scheduling.",
};

export function OnboardingChecklist({ items }: { items: OnboardingItem[] }) {
  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Onboarding checklist</h2>
      <ul className="divide-y divide-ele-hairline-soft">
        {items.map((item) => (
          <li key={item.key} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2">
            <span className="text-sm text-ele-strong">{LABEL[item.key]}</span>
            <span className="flex flex-wrap items-center gap-2">
              {item.notApplicable ? (
                <StatusPill tone="grey" word="Not applicable" title="Not applicable for this billing type" />
              ) : item.key === "rfid_tags" && item.progress ? (
                <StatusPill
                  tone={item.done ? "green" : item.progress.done > 0 ? "amber" : "grey"}
                  word={`${item.progress.done} of ${item.progress.total} tagged`}
                />
              ) : item.done ? (
                <>
                  <StatusPill tone="green" word="Done" />
                  {item.at && <span className="text-xs text-ele-body">{formatDate(item.at)}</span>}
                </>
              ) : (
                <StatusPill tone="grey" word="Not yet" />
              )}
            </span>
            {NOTE[item.key] && <span className="w-full text-xs text-ele-body opacity-70">{NOTE[item.key]}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}
